import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { service } from '@ember/service'
import { uniqBy } from 'lodash'
import { isConflictError } from 'teacher-ui-client/resource-conflict/error'
import type Student from 'teacher-ui-client/models/student'
import type SessionService from 'teacher-ui-client/services/session'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type ResourceConflictService from 'teacher-ui-client/services/resource-conflict'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import { SchoolClassSelect } from 'teacher-ui-client/components/base/select/school-class/index'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import {
  FormWrapperSubmitButton,
  type ExternalSubmitState,
} from 'teacher-ui-client/components/form-wrapper/submit-button'
import { on } from '@ember/modifier'
import { t } from 'ember-intl'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { optional } from 'teacher-ui-client/helpers/optional'
import { not } from 'ember-truth-helpers'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import type Owner from '@ember/owner'

interface Args {
  students: Student[]
  subscriptionType: SubscriptionType
  onSubmit: (students: Student[], classId: string) => unknown
  onCancel?: () => unknown
}

export class FormWrapperRemoveFromClassForm extends Component<Args> {
  @service resourceConflict!: ResourceConflictService

  @service session!: SessionService

  @tracked
  submitState: ExternalSubmitState = 'default'

  constructor(owner: Owner, args: Args) {
    super(owner, args)
    // If there's only 1 option to select, select it by default.
    if (this.schoolClassesForSelect.length === 1 && this.schoolClassesForSelect[0].id) {
      this.selectedClassId = this.schoolClassesForSelect[0].id
    }
  }

  get isSubco() {
    return this.session.currentTeacher.subcoFor(this.args.subscriptionType)
  }

  /**
   * Returns a list of school classes that the current teacher teaches (or all classes if they're a subco).
   */
  get schoolClassesForSelect(): SchoolClass[] {
    const options = this.args.students.reduce<SchoolClass[]>((list, student) => {
      const schoolClasses = student.schoolClasses.filter((sc) => {
        return (
          sc.subscriptionType === this.args.subscriptionType &&
          (this.isSubco || sc.teachers?.includes(this.session.currentTeacher))
        )
      })
      return [...list, ...schoolClasses]
    }, [])

    return uniqBy(options, 'id').sort((a, b) => a.name.localeCompare(b.name))
  }

  /**
   * The tracked property that holds the class id of a class selected by the user in the dropdown.
   */
  @tracked
  selectedClassId?: string

  get selectedStudentsInChosenClass() {
    if (!this.selectedClassId) return []

    const chosenClassId = String(this.selectedClassId)

    return this.args.students.filter((student) => {
      const studentClassIds = student.schoolClasses.map((item) => item.id)
      return studentClassIds.some((id) => String(id) === chosenClassId)
    })
  }

  @action
  updateSchoolClassId(classId: string) {
    this.selectedClassId = classId
  }

  @action
  async onSubmit(event: Event) {
    event.preventDefault()

    this.submitState = 'load'
    if (!this.selectedClassId) return

    try {
      await this.args.onSubmit(this.selectedStudentsInChosenClass, this.selectedClassId)

      // Keep the modal open for a bit before calling the close method
      const timeout = 1100
      window.setTimeout(() => {
        if (this && !this.isDestroying && !this.isDestroyed) this.args.onCancel?.()
      }, timeout)

      this.submitState = 'done'
    } catch (error: any) {
      if (isConflictError(error)) {
        return this.resourceConflict.handleError('Conflict error', error)
      }
      this.submitState = 'error'
    }
  }
  <template>
    <form {{on "submit" this.onSubmit}}>
      <div class="flex items-baseline p-4">
        <p data-test-remove-from-class-text class="mr-2 mb-3">
          {{t "manage.manageClasses.students.bulkActions.totalSelectedStudents" count=@students.length htmlSafe=true}}
          {{#if this.selectedStudentsInChosenClass.length}}
            {{t
              "manage.manageClasses.students.bulkActions.removeFromClass.selectedStudentsInChosenClass"
              count=this.selectedStudentsInChosenClass.length
              htmlSafe=true
            }}
          {{else}}
            {{t "manage.manageClasses.students.bulkActions.removeFromClass.chooseClassLabel"}}
          {{/if}}
        </p>
        <SchoolClassSelect
          class="min-w-0 grow basis-0 truncate"
          @placeholder={{t "manage.manageClasses.students.bulkActions.removeFromClass.classSelectDefaultOption"}}
          @schoolClasses={{this.schoolClassesForSelect}}
          @schoolClassId={{this.selectedClassId}}
          @updateSchoolClassId={{this.updateSchoolClassId}}
          @themeKey={{themeKey @subscriptionType}}
        />
      </div>
      <div class="border-dusty-black-50 border-t p-2 text-right">
        <UiButton data-test-cancel type="reset" {{on "click" (optional @onCancel)}}>
          {{t "manage.manageClasses.students.bulkActions.cancel"}}
        </UiButton>
        <FormWrapperSubmitButton
          data-test-submit
          @defaultText={{t "manage.manageClasses.students.bulkActions.remove"}}
          @state={{this.submitState}}
          @disabled={{not this.selectedClassId}}
          @appearance={{ButtonAppearance.regular}}
        />
      </div>
    </form>
  </template>
}

export default FormWrapperRemoveFromClassForm
